import locationAPI from '@api/jooicez-backend/location';
import { IFindCategoryResponse, IFindCountryResponse } from '@models/location/response';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ISynupConstantsContext {
  isCategoriesLoading: boolean;
  categories: IFindCategoryResponse[];
  //Countries
  isCountriesLoading: boolean;
  countries: IFindCountryResponse[];
}

export enum SynupAvailableCountries {
  UNITED_STATES = 'US',
  CANADA = 'CA',
  INDIA = 'IN',
  ITALY = 'IT',
  MEXICO = 'MX',
  NIGERIA = 'NG',
  SOUTH_AFRICA = 'ZA',
  SPAIN = 'ES',
  UNITED_ARAB_EMIRATES = 'AE',
}

const SynupContstantsContext = createContext<ISynupConstantsContext>({
  // Categories
  isCategoriesLoading: false,
  categories: [],
  // Countries
  isCountriesLoading: false,
  countries: [],
});

export const SynupContstantsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();

  // Categories (API localizes names via Accept-Language; cache must key on locale)
  const [categories, setCategories] = useState<IFindCategoryResponse[]>([]);
  const { data: categoriesData, isFetching: isCategoriesLoading } = useQuery({
    queryKey: ['getCategories', i18n.language],
    queryFn: async () => (await locationAPI.categories()).data,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!categoriesData) return;
    const uniqueCategories = categoriesData.reduce((unique: typeof categories, category: IFindCategoryResponse) => {
      const exists = unique.some((item) => item.name === category.name);
      if (!exists) {
        unique.push(category);
      }
      return unique;
    }, []);
    setCategories(uniqueCategories);
  }, [categoriesData]);

  //countries
  const [countries, setCountries] = useState<IFindCountryResponse[]>([]);
  const { data: countriesData, isFetching: isCountriesLoading } = useQuery({
    queryKey: ['getCountries', i18n.language],
    queryFn: async () => (await locationAPI.countries()).data,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!countriesData) return;
    const us = countriesData.find((x: IFindCountryResponse) => x.iso === 'US');
    const rest = countriesData.filter((x: IFindCountryResponse) => x.iso !== 'US');
    setCountries(us ? [us, ...rest] : rest);
  }, [countriesData]);

  return (
    <SynupContstantsContext.Provider value={{ countries, categories, isCategoriesLoading, isCountriesLoading }}>
      {children}
    </SynupContstantsContext.Provider>
  );
};

export function useSynupConstants() {
  const context = useContext(SynupContstantsContext);
  if (context === undefined) {
    throw new Error('synupConstants must be used within a ListingContextProvider');
  }
  return context;
}
